import { AsyncPipe, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import {
  NgLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';
import { BehaviorSubject, combineLatest, EMPTY, Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import {
  CompareBudgetComponent,
  CompareBudgetVersion,
  CompareGridData,
} from '@widgets/compare-budget/compare-budget.component';
import { CompareBudgetService } from '@widgets/compare-budget/services/compare-budget.service';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { VendorDropdownComponent } from '@shared/components/vendor-dropdown/vendor-dropdown.component';
import { NoopValueAccessorDirective } from '@shared/directives/noop-value-accessor.directive';
import { BudgetType, Currency, GqlService } from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { OrganizationStore } from '@shared/store/organization/organization.store';
import { Utils } from '@shared/utils/utils';

interface CompareBudgetVersionList {
  budget_name: string;
  budget_type: string;
  budget_version_id: string;
  create_date: string;
  version: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NoopValueAccessorDirective,
    VendorDropdownComponent,
    FormsModule,
    ReactiveFormsModule,
    NgSelectComponent,
    NgLabelTemplateDirective,
    NgOptionTemplateDirective,
    IconComponent,
    NgStyle,
    CheckboxComponent,
    ExportExcelButtonComponent,
    CompareBudgetComponent,
    AsyncPipe,
  ],
  providers: [CompareBudgetService],
  selector: 'aux-compare-new',
  styleUrls: ['./compare-new.component.css'],
  templateUrl: './compare-new.component.html',
})
export class CompareNewComponent implements OnInit {
  organizationQuery = inject(OrganizationQuery);

  private compareBudgetService = inject(CompareBudgetService);
  private destroyRef = inject(DestroyRef);
  private gqlService = inject(GqlService);
  private launchDarklyService = inject(LaunchDarklyService);
  private organizationService = inject(OrganizationService);
  private organizationStore = inject(OrganizationStore);

  budgetVersionList$ = new BehaviorSubject<CompareBudgetVersionList[]>([]);
  budgetVersionToList$ = new BehaviorSubject<CompareBudgetVersionList[]>([]);
  currencySymbol = '$';
  fromBudgetVersion$ = new BehaviorSubject<CompareBudgetVersion | null>(null);
  loading$ = new BehaviorSubject(false);
  orgCurrency = Currency.USD;
  selectedFrom = new UntypedFormControl('');
  selectedTo = new UntypedFormControl('');
  selectedVendor = new UntypedFormControl('');
  showAnalyticsSection$: Observable<boolean>;
  toBudgetVersion$ = new BehaviorSubject<CompareBudgetVersion | null>(null);
  totals = {
    category_amount_AC: Utils.zeroHyphen,
    category_type_AC: Utils.zeroHyphen,
    from_total_cost: 0,
    investigator_total_AC: `${this.currencySymbol}0`,
    investigator_total_percent_AC: 0,
    investigator_total_percent_AC_Formatted: '0%',
    pass_through_total_AC: `${this.currencySymbol}0`,
    pass_through_total_percent_AC: 0,
    pass_through_total_percent_AC_Formatted: '0%',
    services_total_AC: `${this.currencySymbol}0`,
    services_total_percent_AC: 0,
    services_total_percent_AC_Formatted: '0%',
    to_total_cost: 0,
    variance_percentage_AC: '0',
    variance_total_cost: 0,
    variance_total_cost_AC: '0',
  };

  ngOnInit(): void {
    this.organizationService
      .get()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const vendors = this.organizationQuery.getAllVendors();

        if (vendors.length === 1) {
          this.organizationStore.setActive(vendors[0].id);
          this.selectedVendor.setValue(vendors[0].id);

          if (vendors[0].currency) {
            this.orgCurrency = Currency[vendors[0].currency];
            this.setOrgCurrencySymbol();
            this.setDefaultTotals();
          }
        } else {
          // reset any older selected vendors.
          this.organizationStore.setActive(null);
          this.selectedVendor.setValue('');
        }
      });

    this.handleSubscribe();
  }

  acCurrFormat(v: number) {
    return Utils.currencyFormatter(
      v,
      {
        currencySign: 'accounting',
      },
      this.orgCurrency
    );
  }

  acPercentFormat(v: number) {
    return Utils.agPercentageFormatterAbsolute(
      {
        value: v,
      },
      { maximumFractionDigits: 0, minimumFractionDigits: 0 }
    );
  }

  compareBudget() {
    const from = this.selectedFrom.value as CompareBudgetVersionList;
    const to = this.selectedTo.value as CompareBudgetVersionList;

    this.fromBudgetVersion$.next({
      budget_name: `${from.budget_name} (Baseline)`.replace('(Current) ', ''),
      budget_type:
        ['Budget Upload', 'Change Order'].indexOf(from.budget_type) !== -1
          ? BudgetType.BUDGET_PRIMARY
          : BudgetType.BUDGET_SECONDARY,
      budget_version_id: from.budget_version_id,
    });

    this.toBudgetVersion$.next({
      budget_name: `${to.budget_name}`.replace('(Current)', ''),
      budget_type:
        ['Budget Upload', 'Change Order'].indexOf(to.budget_type) !== -1
          ? BudgetType.BUDGET_PRIMARY
          : BudgetType.BUDGET_SECONDARY,
      budget_version_id: to.budget_version_id,
    });
  }

  constructor() {
    this.disableDateControls();
    this.showAnalyticsSection$ = this.launchDarklyService.select$(
      (flags) => flags.section_compare_analytics
    );
    this.organizationQuery
      .selectActive()
      .pipe(
        switchMap((org) => {
          if (org) {
            this.loading$.next(true);

            return combineLatest([
              this.gqlService.listBudgetVersions$([BudgetType.BUDGET_PRIMARY], org.id),
              this.gqlService.listBudgetVersions$([BudgetType.BUDGET_SECONDARY], org.id),
            ]).pipe(
              tap(([primary, secondary]) => {
                let max_create_date = Number.MIN_SAFE_INTEGER;
                let max_create_id = '';

                let primaryData = (primary.data || []).map((x) => {
                  if (new Date(x.create_date).valueOf() > max_create_date) {
                    max_create_id = x.budget_version_id;
                    max_create_date = new Date(x.create_date).valueOf();
                  }

                  return {
                    budget_name: x.is_change_order
                      ? Utils.replaceChangeOrderFormat(x.budget_name)
                      : x.budget_name,
                    budget_type: x.is_change_order ? 'Change Order' : 'Budget Upload',
                    budget_version_id: x.budget_version_id,
                    create_date: Utils.dateFormatter(x.create_date),
                    version: x.version,
                  };
                });

                // Adds (Current) to the latest budget version
                primaryData = primaryData.map((x) => {
                  return {
                    budget_name: `${x?.budget_name}${
                      x.budget_version_id === max_create_id ? ' (Current)' : ''
                    }`,
                    budget_type: x.budget_type,
                    budget_version_id: x?.budget_version_id,
                    create_date: x.create_date,
                    version: x?.version,
                  };
                });
                const secondaryData = (secondary.data || []).map((x) => {
                  return {
                    budget_name: x.budget_name,
                    budget_type: 'Scenario Budget',
                    budget_version_id: x.budget_version_id,
                    create_date: Utils.dateFormatter(x.create_date),
                    version: x.version,
                  };
                });

                this.handleSubscribe();
                this.budgetVersionList$.next([...primaryData, ...secondaryData]);
                this.loading$.next(false);
              })
            );
          }

          return EMPTY;
        })
      )
      .pipe(takeUntilDestroyed())
      .subscribe();

    this.selectedVendor.valueChanges.pipe(takeUntilDestroyed()).subscribe((x) => {
      if (x) {
        this.selectedFrom.enable();
      }
    });
  }

  disableDateControls() {
    this.selectedFrom.disable();
    this.selectedTo.disable();
  }

  handleSubscribe() {
    this.selectedFrom.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((x) => {
      this.selectedTo.enable();
      const toList = this.budgetVersionList$
        .getValue()
        .filter((z) => z.budget_version_id !== x.budget_version_id);

      this.budgetVersionToList$.next(toList);
    });
  }

  onBudgetData(arr: CompareGridData[]) {
    // do some calculations with array and update the totals in this component
    const diffs = this.compareBudgetService.getCompareDiffs(arr);

    this.totals.variance_total_cost_AC = this.acCurrFormat(diffs.variance_total_cost);
    this.totals.variance_percentage_AC = this.acPercentFormat(diffs.variance_total_cost_percentage);
    this.totals.services_total_AC = this.acCurrFormat(diffs.delta_services);
    this.totals.services_total_percent_AC = diffs.delta_services / diffs.variance_total_cost;
    this.totals.services_total_percent_AC_Formatted = this.acPercentFormat(
      this.totals.services_total_percent_AC
    );
    this.totals.investigator_total_AC = this.acCurrFormat(diffs.delta_investigator);
    this.totals.investigator_total_percent_AC =
      diffs.delta_investigator / diffs.variance_total_cost;
    this.totals.investigator_total_percent_AC_Formatted = this.acPercentFormat(
      this.totals.investigator_total_percent_AC
    );
    this.totals.pass_through_total_AC = this.acCurrFormat(diffs.delta_pass_through);
    this.totals.pass_through_total_percent_AC =
      diffs.delta_pass_through / diffs.variance_total_cost;
    this.totals.pass_through_total_percent_AC_Formatted = this.acPercentFormat(
      this.totals.pass_through_total_percent_AC
    );
    this.totals.category_type_AC = diffs.max_diff_activity;
    this.totals.category_amount_AC = this.acCurrFormat(diffs.max_activity_diff);
  }

  onVendorSelected(vendorId: string) {
    this.budgetVersionList$.next([]);
    this.budgetVersionToList$.next([]);
    this.selectedFrom = new UntypedFormControl('');
    this.selectedTo = new UntypedFormControl('');
    this.organizationStore.setActive(vendorId || null);
    const activeOrg = this.organizationQuery.getActive();

    if (activeOrg?.currency) {
      this.orgCurrency = Currency[activeOrg.currency];
      this.setOrgCurrencySymbol();
      this.setDefaultTotals();
    }
  }

  setDefaultTotals() {
    this.totals = {
      category_amount_AC: Utils.zeroHyphen,
      category_type_AC: Utils.zeroHyphen,
      from_total_cost: 0,
      investigator_total_AC: `${this.currencySymbol}0`,
      investigator_total_percent_AC: 0,
      investigator_total_percent_AC_Formatted: '0%',
      pass_through_total_AC: `${this.currencySymbol}0`,
      pass_through_total_percent_AC: 0,
      pass_through_total_percent_AC_Formatted: '0%',
      services_total_AC: `${this.currencySymbol}0`,
      services_total_percent_AC: 0,
      services_total_percent_AC_Formatted: '0%',
      to_total_cost: 0,
      variance_percentage_AC: '0',
      variance_total_cost: 0,
      variance_total_cost_AC: '0',
    };
  }

  setOrgCurrencySymbol() {
    const currency: string = Currency[this.orgCurrency];
    const l = Utils.getCurrenySymbol(currency as Currency);

    if (l) {
      this.currencySymbol = l;
    }
  }
}
